import React, {
    useState,
    useEffect,
    useReducer,
    createContext,
    useContext,
} from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import {
    ThemeProvider,
    StyledEngineProvider,
    createTheme,
} from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import withStyles from '@mui/styles/withStyles';
import debounce from 'lodash/debounce';
import { useRoute, useRouter } from 'react-router5';
import Icon from '@mui/material/Icon';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import InputLabel from '@mui/material/InputLabel';
import Snackbar from '@mui/material/Snackbar';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Grid from '@mui/material/Grid';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import { format, addDays, addYears, formatDistanceToNow } from 'date-fns';
import Dropzone from 'react-dropzone';

import logo from './cloudcraft.svg';
import UserTable from './UserTable';
import CustomerTable from './CustomerTable';
import MemberTable from './MemberTable';
import CredentialTable from './CredentialTable';
import CopyButton from './CopyButton';
import Api from './adminapiclient';
import CustomerReport from './CustomerReport.js';

const theme = createTheme();

let datadogAppUrl = 'https://app.datadoghq.com';
if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
    datadogAppUrl = 'https://app.datad0g.com';
}

const useStyles = makeStyles((theme) => ({
    app: {
        textAlign: 'center',
    },
    header: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
        marginTop: 10,
        cursor: 'pointer',
    },
    logo: {
        height: 64,
        pointerEvents: 'none',
    },
    body: {
        marginTop: 40,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'start',
        width: '100%',
    },
    textField: {
        width: 400,
    },
    groupHeader: {
        paddingTop: 24,
    },
    errorMessage: {
        paddingTop: 24,
        color: 'red',
    },
    groupContainer: {
        padding: 24,
    },
    button: {
        marginLeft: 6,
        marginRight: 6,
    },
    rightButton: {
        marginLeft: 'auto',
        marginRight: 6,
    },
    rightIcon: {
        marginLeft: 12,
    },
    rightButtons: {
        marginLeft: 'auto',
    },
    toolbar: {
        width: '100%',
        padding: 12,
        display: 'flex',
        flexDirection: 'center',
    },
    close: {
        padding: 12,
    },
}));

const DialogSubheader = withStyles({
    h3: {
        color: 'rgba(0, 0, 0, 0.87)',
        fontSize: 12,
        fontWeight: 600,
        marginTop: 12,
        marginBottom: 12,
    },
})(Typography);

const adminUiPlansProd = [
    { planId: 'free', displayName: 'Free' },
    {
        planId: 'enterprise_annual_invoiced',
        displayName: 'Enterprise Annual Invoiced',
    },
    {
        planId: 'enterprise_monthly_invoiced',
        displayName: 'Enterprise Monthly Invoiced',
    },
    { planId: 'pro_annual_invoiced', displayName: 'Pro Annual Invoiced' },
    { planId: 'pro_annual', displayName: 'Pro Annual (Stripe $490)' },
    { planId: 'pro_monthly_invoiced', displayName: 'Pro Monthly Invoiced' },
    { planId: 'pro_monthly_2023_02', displayName: 'Pro Monthly (Stripe $99)' },
    {
        planId: 'pro_aws_marketplace_annual',
        displayName: 'Pro Annual via AWS Marketplace',
    },
    { planId: 'vip', displayName: 'VIP - Complimentary access' },
];

const adminUiPlansLegacy = [
    { planId: 'pro_monthly', displayName: 'Pro Monthly (Legacy Stripe $49)' },
    {
        planId: 'enterprise_unlimited_annual',
        displayName: 'Enterprise Annual Unlimited (Legacy)',
    },
    { planId: 'enterprise_annual', displayName: 'Enterprise Annual (Legacy)' },
];

const adminUiPlansDev = [
    {
        planId: 'pro_monthly_test_2023_02',
        displayName: 'Pro Monthly (new $99) Dev Env',
    },
];

const adminUiInvoicedIds = [
    'enterprise_annual_invoiced',
    'enterprise_monthly_invoiced',
    'pro_annual_invoiced',
    'pro_monthly_invoiced',
    'vip',
    'enterprise_unlimited_annual',
    'enterprise_annual',
];

function getPlans(includeLegacyPlans = false, includeSelfServePlans = true) {
    let plans = [...adminUiPlansProd];

    if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
        plans.push(...adminUiPlansDev);
    }

    if (includeLegacyPlans) {
        plans.push(...adminUiPlansLegacy);
    }

    if (!includeSelfServePlans) {
        plans = plans.filter((plan) =>
            adminUiInvoicedIds.includes(plan.planId),
        );
    }

    return plans;
}

function prettyTime(dateString) {
    try {
        return formatDistanceToNow(Date.parse(dateString), { addSuffix: true });
    } catch (e) {
        return '?';
    }
}

function endDateDaysFromNow(days) {
    const date = addDays(new Date(), days);
    const endDate = format(date, "yyyy-MM-dd'T'HH:mm");

    return endDate;
}

function endDateYearsFromNow(years) {
    const date = addYears(new Date(), years);
    const endDate = format(date, "yyyy-MM-dd'T'HH:mm");

    return endDate;
}

function ErrorMessage({ text, error }) {
    const classes = useStyles();

    return (
        <Typography variant="h5" className={classes.errorMessage}>
            <span style={{ color: 'red' }}>{text}</span>
            <p style={{ color: 'grey' }}>{error.toString()}</p>
        </Typography>
    );
}

function Header({ title, icon }) {
    const classes = useStyles();

    return (
        <Typography variant="h4" className={classes.groupHeader}>
            {icon && (
                <Icon
                    style={{
                        fontSize: 38,
                        verticalAlign: 'top',
                        marginRight: 8,
                    }}
                >
                    {icon}
                </Icon>
            )}
            {title}
        </Typography>
    );
}

function Form({ id, children }) {
    const classes = useStyles();

    return (
        <form
            id={id}
            className={classes.form}
            noValidate
            onSubmit={(e) => e.preventDefault()}
        >
            {children}
        </form>
    );
}

function Notifications({ notification }) {
    const dispatch = useContext(AppDispatch);
    const classes = useStyles();

    return (
        <div>
            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                open={!!notification}
                autoHideDuration={4000}
                onClose={() => dispatch({ type: 'notify', message: undefined })}
                ContentProps={{
                    'aria-describedby': 'message-id',
                }}
                message={<span id="message-id">{notification}</span>}
                action={[
                    <IconButton
                        key="close"
                        aria-label="Close"
                        color="inherit"
                        className={classes.close}
                        onClick={() =>
                            dispatch({ type: 'notify', message: undefined })
                        }
                        size="large"
                    >
                        <Icon
                            style={{
                                fontSize: 38,
                                verticalAlign: 'top',
                                marginRight: 8,
                            }}
                        >
                            close
                        </Icon>
                    </IconButton>,
                ]}
            />
        </div>
    );
}

function GroupHeader({ title, icon }) {
    const classes = useStyles();

    return (
        <Typography variant="h6" className={classes.groupHeader}>
            {icon && (
                <Icon
                    style={{
                        fontSize: 32,
                        verticalAlign: 'top',
                        marginRight: 8,
                    }}
                >
                    {icon}
                </Icon>
            )}
            {title}
        </Typography>
    );
}

function SubHeader({ title }) {
    return <Typography variant="subtitle1">{title}</Typography>;
}

function SearchResults(props) {
    const { searchResults, onSelectUser, onSelectCustomer } = props;
    const { users, customers } = searchResults;

    if (
        !searchResults ||
        (searchResults &&
            users.length === 0 &&
            (!customers || customers.length === 0))
    ) {
        return <Header title="No results" />;
    }

    return (
        <div>
            {users.length > 0 && (
                <Header
                    title={`${users.length} user${
                        users.length === 1 ? '' : 's'
                    }`}
                    icon="person"
                />
            )}
            <UserTable users={users} onSelectUser={onSelectUser} />
            {customers && customers.length > 0 && (
                <Header
                    title={`${customers.length} customer${
                        customers.length === 1 ? '' : 's'
                    }`}
                    icon="business"
                />
            )}
            <CustomerTable
                customers={customers}
                onSelectCustomer={onSelectCustomer}
                initialState={{
                    columns: {
                        columnVisibilityModel: {
                            id: false,
                            ownerName: false,
                            ownerEmail: false,
                            awsMarketplaceId: false,
                            updatedAt: false,
                        },
                    },
                }}
            />
        </div>
    );
}

async function getUserByEmail(email, dispatch) {
    try {
        const response = await Api.search(email, undefined, false);
        return response.data.users;
    } catch (error) {
        dispatch({ type: 'notify', message: `Getting user failed: ${error}` });
    }
}

async function createCustomer(customer, dispatch) {
    try {
        const response = await Api.createCustomer(customer);
        if (response.status === 201) {
            dispatch({ type: 'notify', message: 'Customer created' });
            return response.data;
        } else {
            dispatch({
                type: 'notify',
                message: `Customer create failed: ${response.status} ${
                    (response.data && response.data.error) ||
                    response.statusText ||
                    response.statusMessage
                }`,
            });
        }
    } catch (error) {
        dispatch({
            type: 'notify',
            message: `Customer create failed: ${error}`,
        });
    }
}

async function updateCustomer(customer, dispatch, message) {
    try {
        const response = await Api.updateCustomer(customer);
        if (response.status === 200) {
            dispatch({
                type: 'notify',
                message: message || 'Customer updated',
            });
            return response.data;
        } else {
            dispatch({
                type: 'notify',
                message: `Customer update failed: ${response.status} ${
                    (response.data && response.data.error) ||
                    response.statusText ||
                    response.statusMessage
                }`,
            });
        }
    } catch (error) {
        dispatch({
            type: 'notify',
            message: `Customer update failed: ${error}`,
        });
    }
}

async function mergeCustomer(targetCustomerId, sourceCustomerId, dispatch) {
    try {
        const response = await Api.mergeCustomers(
            targetCustomerId,
            sourceCustomerId,
        );
        if (response.status === 204) {
            window.location.reload();
            dispatch({ type: 'notify', message: 'Customer merged' });
        } else {
            dispatch({
                type: 'notify',
                message: `Customer merge failed: ${response.status} ${
                    (response.data && response.data.error) ||
                    response.statusText ||
                    response.statusMessage
                }`,
            });
        }
    } catch (error) {
        dispatch({
            type: 'notify',
            message: `Customer merge failed: ${error}`,
        });
    }
}

async function updateUser(user, dispatch) {
    try {
        const response = await Api.updateUser(user);
        if (response.status === 200) {
            dispatch({ type: 'notify', message: 'User updated' });
            return response.data;
        } else {
            dispatch({
                type: 'notify',
                message: `User update failed: ${response.status} ${
                    (response.data && response.data.error) ||
                    response.statusText ||
                    response.statusMessage
                }`,
            });
        }
    } catch (error) {
        dispatch({ type: 'notify', message: `User update failed: ${error}` });
    }
}

async function createSsoConfig(customerId, ssoConfig, dispatch) {
    try {
        const response = await Api.createSsoConfig(customerId, ssoConfig);
        if (response.status === 201) {
            dispatch({ type: 'notify', message: 'SSO config created' });
            return response.data;
        } else {
            dispatch({
                type: 'notify',
                message: `SSO config create failed: ${response.status} ${
                    (response.data && response.data.error) ||
                    response.statusText ||
                    response.statusMessage
                }`,
            });
        }
    } catch (error) {
        dispatch({
            type: 'notify',
            message: `SSO config create failed: ${error}`,
        });
    }
}

async function updateSsoConfig(customerId, ssoConfig, dispatch) {
    try {
        const response = await Api.updateSsoConfig(customerId, ssoConfig);
        if (response.status === 200) {
            dispatch({ type: 'notify', message: 'SSO config updated' });
            return response.data;
        } else {
            dispatch({
                type: 'notify',
                message: `SSO config update failed: ${response.status} ${
                    (response.data && response.data.error) ||
                    response.statusText ||
                    response.statusMessage
                }`,
            });
        }
    } catch (error) {
        dispatch({
            type: 'notify',
            message: `SSO config update failed: ${error}`,
        });
    }
}

async function createTeam(team, dispatch) {
    try {
        const response = await Api.createTeam(team);
        if (response.status === 201) {
            dispatch({ type: 'notify', message: 'Team created' });
            return response.data;
        } else {
            dispatch({
                type: 'notify',
                message: `Team create failed: ${response.status} ${
                    (response.data && response.data.error) ||
                    response.statusText ||
                    response.statusMessage
                }`,
            });
        }
    } catch (error) {
        dispatch({ type: 'notify', message: `Team create failed: ${error}` });
    }
}

async function updateTeam(team, dispatch) {
    try {
        const response = await Api.updateTeam(team);
        if (response.status === 200) {
            dispatch({ type: 'notify', message: 'Team updated' });
            return response.data;
        } else {
            dispatch({
                type: 'notify',
                message: `Team update failed: ${response.status} ${
                    (response.data && response.data.error) ||
                    response.statusText ||
                    response.statusMessage
                }`,
            });
        }
    } catch (error) {
        dispatch({ type: 'notify', message: `Team update failed: ${error}` });
    }
}

function CustomerDialog(props) {
    const dispatch = useContext(AppDispatch);
    const classes = useStyles();
    const { open, onClose, user, customer } = props;
    const [name, setName] = useState();
    const [ddOrgId, setDdOrgId] = useState();
    const [ddDomain, setDdDomain] = useState();
    const [stripeId, setStripeId] = useState();
    const [awsMarketplaceId, setAwsMarketplaceId] = useState();
    const [externalSharing, setExternalSharing] = useState();
    const [planId, setPlanId] = useState();
    const [quantity, setQuantity] = useState();
    const [activeUntil, setActiveUntil] = useState();
    const [trial, setTrial] = useState();
    const [ddsqlEnabled, setDdsqlEnabled] = useState();

    useEffect(() => {
        setName(customer ? customer.name : '');
        setDdOrgId(customer ? customer.ddOrgId : '');
        setDdDomain(customer ? customer.ddDomain : '');
        setStripeId(customer ? customer.stripeId : '');
        setAwsMarketplaceId(customer ? customer.awsMarketplaceId : '');
        setExternalSharing(customer ? customer.externalSharing : true);
        setPlanId(customer ? customer.planId : 'pro_annual_invoiced');
        setQuantity(customer ? customer.quantity : 1);
        setTrial(customer ? customer.trial : false);
        setDdsqlEnabled(customer?.features?.includes?.('ddsql'));

        let defaultEndDate;
        if (customer) {
            const date = new Date(customer.activeUntil);
            defaultEndDate = format(date, "yyyy-MM-dd'T'HH:mm");
        } else {
            const date = addDays(new Date(), 14);
            defaultEndDate = format(date, "yyyy-MM-dd'T'HH:mm");
        }
        setActiveUntil(defaultEndDate);
    }, [customer]);

    const router = useRouter();
    const planEditingDisabled = !!customer && !!stripeId;
    const isDatadogSSO = Boolean(ddOrgId && ddDomain);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title">
                {customer ? 'Edit customer' : 'Create customer'}
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {user && (
                        <div>
                            Create a new Customer for the user{' '}
                            <Typography color="primary" component="span" inline>
                                {user.name}
                            </Typography>
                        </div>
                    )}
                </DialogContentText>
                <Form id="customer-form">
                    <TextField
                        variant="standard"
                        autoFocus
                        margin="normal"
                        id="name"
                        label="Customer name"
                        type="text"
                        placeholder="Company"
                        autoComplete="off"
                        style={{ width: 350 }}
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                    />
                    <TextField
                        variant="standard"
                        margin="normal"
                        id="orgId"
                        label="Datadog org UUID"
                        type="text"
                        placeholder="Datadog org UUID"
                        autoComplete="off"
                        style={{ width: 350 }}
                        value={ddOrgId}
                        onChange={(event) =>
                            setDdOrgId(event.target.value.trim())
                        }
                    />
                    <TextField
                        variant="standard"
                        margin="normal"
                        id="orgId"
                        label="Datadog app domain"
                        type="text"
                        placeholder="app.datadoghq.com"
                        autoComplete="off"
                        style={{ width: 350 }}
                        value={ddDomain}
                        onChange={(event) =>
                            setDdDomain(event.target.value.trim())
                        }
                    />
                    <TextField
                        variant="standard"
                        margin="normal"
                        id="stripeId"
                        label="Stripe ID"
                        type="text"
                        placeholder="Stripe ID"
                        autoComplete="off"
                        style={{ width: 350 }}
                        value={stripeId}
                        onChange={(event) =>
                            setStripeId(event.target.value.trim())
                        }
                    />
                    <TextField
                        variant="standard"
                        margin="normal"
                        id="awsMarketplaceId"
                        label="AWS Marketplace ID"
                        type="text"
                        placeholder="AWS Marketplace ID"
                        autoComplete="off"
                        style={{ width: 350 }}
                        value={awsMarketplaceId}
                        onChange={(event) =>
                            setAwsMarketplaceId(event.target.value.trim())
                        }
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={externalSharing}
                                onChange={(event) =>
                                    setExternalSharing(event.target.checked)
                                }
                            />
                        }
                        label="External sharing enabled"
                    />
                    <Tooltip
                        disableHoverListener={!planEditingDisabled}
                        title="Must be updated in Stripe when a Stripe ID is defined"
                    >
                        <FormControl
                            variant="standard"
                            className={classes.formControl}
                            margin="normal"
                            disabled={planEditingDisabled}
                        >
                            <InputLabel>Plan</InputLabel>
                            <Select
                                variant="standard"
                                value={planId}
                                onChange={(event) =>
                                    setPlanId(event.target.value)
                                }
                                style={{ minWidth: 200 }}
                            >
                                {getPlans(true).map((plan) => (
                                    <MenuItem
                                        key={plan.planId}
                                        value={plan.planId}
                                    >
                                        {plan.displayName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Tooltip>
                    <Tooltip
                        disableHoverListener={!planEditingDisabled}
                        title="Must be updated in Stripe when a Stripe ID is defined"
                    >
                        <TextField
                            variant="standard"
                            margin="normal"
                            id="quantity"
                            label="Number of seats"
                            type="number"
                            value={quantity}
                            style={{ width: 230 }}
                            onChange={(event) =>
                                setQuantity(
                                    parseInt(event.target.value, 10) || '',
                                )
                            }
                            disabled={planEditingDisabled}
                        />
                    </Tooltip>
                    <InputLabel
                        style={{
                            transform: 'translate(0px, 1.5px) scale(0.75)',
                            marginTop: 16,
                        }}
                    >
                        Presets
                    </InputLabel>
                    <div style={{ display: 'flex', flexDirection: 'row' }}>
                        <Button
                            disabled={planEditingDisabled}
                            onClick={() =>
                                setActiveUntil(endDateDaysFromNow(0))
                            }
                        >
                            <small>Now</small>
                        </Button>
                        <Button
                            disabled={planEditingDisabled}
                            onClick={() =>
                                setActiveUntil(endDateDaysFromNow(14))
                            }
                        >
                            <small>14 days</small>
                        </Button>
                        <Button
                            disabled={planEditingDisabled}
                            onClick={() =>
                                setActiveUntil(endDateDaysFromNow(30))
                            }
                        >
                            <small>30 days</small>
                        </Button>
                        <Button
                            disabled={planEditingDisabled}
                            onClick={() =>
                                setActiveUntil(endDateYearsFromNow(1))
                            }
                        >
                            <small>1 year</small>
                        </Button>
                    </div>
                    <Tooltip
                        disableHoverListener={!planEditingDisabled}
                        title="Must be updated in Stripe when a Stripe ID is defined"
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'end',
                            }}
                        >
                            <TextField
                                variant="standard"
                                margin="normal"
                                label="Subscription end date"
                                type="datetime-local"
                                value={activeUntil}
                                style={{ width: 230 }}
                                onChange={(event) =>
                                    setActiveUntil(event.target.value)
                                }
                                disabled={planEditingDisabled}
                            />
                        </div>
                    </Tooltip>
                    <span>
                        <small>{prettyTime(activeUntil)}</small>
                    </span>
                    <Tooltip
                        disableHoverListener={!planEditingDisabled}
                        title="Must be updated in Stripe when a Stripe ID is defined"
                    >
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={trial}
                                    onChange={(event) =>
                                        setTrial(event.target.checked)
                                    }
                                />
                            }
                            label="Trial"
                            disabled={planEditingDisabled}
                        />
                    </Tooltip>

                    {isDatadogSSO && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={ddsqlEnabled}
                                    onChange={(event) =>
                                        setDdsqlEnabled(event.target.checked)
                                    }
                                />
                            }
                            label="DDSQL/REDAPL integration enabled"
                        />
                    )}
                </Form>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Cancel
                </Button>
                <Button
                    type="submit"
                    form="customer-form"
                    disabled={!name || !planId || !quantity || !activeUntil}
                    onClick={async () => {
                        let result;
                        if (customer) {
                            result = await updateCustomer(
                                {
                                    id: customer.id,
                                    OwnerId: customer.OwnerId,
                                    name,
                                    ddOrgId:
                                        ddOrgId?.length > 0 ? ddOrgId : null,
                                    ddDomain:
                                        ddDomain?.length > 0 ? ddDomain : null,
                                    stripeId:
                                        stripeId?.length > 0 ? stripeId : null,
                                    awsMarketplaceId:
                                        awsMarketplaceId?.length > 0
                                            ? awsMarketplaceId
                                            : null,
                                    externalSharing,
                                    planId,
                                    quantity,
                                    activeUntil,
                                    trial,
                                    ddsqlEnabled,
                                },
                                dispatch,
                            );
                        } else {
                            result = await createCustomer(
                                {
                                    OwnerId: user.id,
                                    name,
                                    ddOrgId:
                                        ddOrgId?.length > 0 ? ddOrgId : null,
                                    ddDomain:
                                        ddDomain?.length > 0 ? ddDomain : null,
                                    stripeId:
                                        stripeId?.length > 0 ? stripeId : null,
                                    awsMarketplaceId:
                                        awsMarketplaceId?.length > 0
                                            ? awsMarketplaceId
                                            : null,
                                    externalSharing,
                                    planId,
                                    quantity,
                                    activeUntil,
                                    trial,
                                },
                                dispatch,
                            );
                        }

                        onClose();
                        if (result) {
                            console.log('NAVIGATE on create', result);
                            router.navigate(
                                'customer',
                                { id: result.id },
                                { reload: true },
                            ); // Check if force needed
                        }
                    }}
                    color="primary"
                >
                    {customer ? 'Save customer' : 'Create customer'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function EditUserDialog(props) {
    const dispatch = useContext(AppDispatch);
    const { onClose, user } = props;

    const [name, setName] = useState();
    const [email, setEmail] = useState();

    useEffect(() => {
        setName(user ? user.name : '');
        setEmail(user ? user.email : '');
    }, [user]);

    const router = useRouter();
    return (
        <Dialog
            open={!!user}
            onClose={onClose}
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title">Edit user</DialogTitle>
            <DialogContent>
                <Form id="customer-form">
                    <TextField
                        variant="standard"
                        autoFocus
                        margin="normal"
                        id="name"
                        label="User name"
                        type="text"
                        placeholder="Name"
                        autoComplete="off"
                        style={{ width: 350 }}
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                    />
                    <TextField
                        variant="standard"
                        margin="normal"
                        id="email"
                        label="Email"
                        type="email"
                        placeholder="Email"
                        autoComplete="off"
                        style={{ width: 350 }}
                        value={email}
                        onChange={(event) =>
                            setEmail(event.target.value.trim())
                        }
                    />
                </Form>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Cancel
                </Button>
                <Button
                    type="submit"
                    form="user-form"
                    disabled={!name || !email}
                    onClick={async () => {
                        await updateUser(
                            {
                                id: user.id,
                                name,
                                email,
                            },
                            dispatch,
                        );
                        onClose();
                        router.navigate(
                            'user',
                            { id: user.id },
                            { reload: true },
                        );
                    }}
                    color="primary"
                >
                    {'Save user'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

async function createTeamMember(teamId, userId, role, dispatch) {
    try {
        const response = await Api.createTeamMember(teamId, { userId, role });
        if (response.status === 201) {
            dispatch({ type: 'notify', message: 'Member created' });
            return response.body;
        } else {
            dispatch({
                type: 'notify',
                message: `Member create failed: ${response.status} ${
                    (response.data && response.data.error) ||
                    response.statusText ||
                    response.statusMessage
                }`,
            });
        }
    } catch (error) {
        dispatch({ type: 'notify', message: `Member create failed: ${error}` });
    }
}

async function inviteTeamMember(teamId, email, role, dispatch) {
    try {
        const response = await Api.inviteTeamMember(teamId, { email, role });
        if (response.status === 201) {
            dispatch({ type: 'notify', message: 'Member invited' });
            return response.body;
        } else {
            dispatch({
                type: 'notify',
                message: `Member invite failed: ${response.status} ${
                    (response.data && response.data.error) ||
                    response.statusText ||
                    response.statusMessage
                }`,
            });
        }
    } catch (error) {
        dispatch({ type: 'notify', message: `Member invite failed: ${error}` });
    }
}

async function updateTeamMember(teamId, memberId, role, dispatch) {
    try {
        const response = await Api.updateTeamMember(teamId, {
            id: memberId,
            role,
        });
        if (response.status === 200) {
            dispatch({ type: 'notify', message: 'Member updated' });
            return response.body;
        } else {
            dispatch({
                type: 'notify',
                message: `Member update failed: ${response.status} ${
                    (response.data && response.data.error) ||
                    response.statusText ||
                    response.statusMessage
                }`,
            });
        }
    } catch (error) {
        dispatch({ type: 'notify', message: `Member update failed: ${error}` });
    }
}

async function removeTeamMember(teamId, memberId, dispatch) {
    try {
        const response = await Api.removeTeamMember(teamId, memberId);
        if (response.status === 204) {
            dispatch({ type: 'notify', message: 'Member removed' });
            return true;
        } else {
            dispatch({
                type: 'notify',
                message: `Member remove failed: ${response.status} ${
                    (response.data && response.data.error) ||
                    response.statusText ||
                    response.statusMessage
                }`,
            });
        }
    } catch (error) {
        dispatch({ type: 'notify', message: `Member remove failed: ${error}` });
    }
}

function MemberDialog(props) {
    const dispatch = useContext(AppDispatch);
    const classes = useStyles();
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState(undefined);
    const { mode, onClose, team, member } = props;
    const [role, setRole] = useState('user');
    const [email, setEmail] = useState('');

    useEffect(() => {
        setRole(member ? member.role : 'user');
    }, [member]);

    const selectUser = async (userId) => {
        const result = await createTeamMember(team.id, userId, role, dispatch);

        onClose(result);
    };

    const inviteUser = async (email) => {
        const result = await inviteTeamMember(team.id, email, role, dispatch);

        onClose(result);
    };

    const updateUserTeam = async () => {
        const result = await updateTeamMember(
            team.id,
            member.id,
            role,
            dispatch,
        );

        onClose(result);
    };

    let dialogTitle;
    switch (mode) {
        case 'invite':
            dialogTitle = 'Invite user by email';
            break;
        case 'add':
            dialogTitle = 'Add direct to team';
            break;
        default:
            dialogTitle = 'Edit member';
            break;
    }

    return (
        <Dialog
            open={!!mode}
            onClose={onClose}
            maxWidth={false}
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title">{dialogTitle}</DialogTitle>
            <DialogContent>
                <FormControl
                    variant="standard"
                    className={classes.formControl}
                    margin="normal"
                >
                    <FormLabel>Role</FormLabel>
                    <RadioGroup
                        value={role}
                        onChange={(event) => setRole(event.target.value)}
                        style={{ minWidth: 200 }}
                    >
                        <FormControlLabel
                            value="owner"
                            control={<Radio />}
                            label="Owner"
                        />
                        <FormControlLabel
                            value="admin"
                            control={<Radio />}
                            label="Admin"
                        />
                        <FormControlLabel
                            value="user"
                            control={<Radio />}
                            label="User"
                        />
                    </RadioGroup>
                </FormControl>

                {mode === 'add' && (
                    <div>
                        <FormControl
                            variant="standard"
                            className={classes.formControl}
                            margin="normal"
                        >
                            <TextField
                                id="outlined-search"
                                label="Search Users"
                                placeholder="Name, email or ID"
                                type="search"
                                className={classes.textField}
                                margin="normal"
                                variant="outlined"
                                autoFocus={true}
                                autoComplete="off"
                                value={searchTerm}
                                onChange={(event) => {
                                    const searchTerm = event.target.value;
                                    setSearchTerm(searchTerm);
                                    debouncedSearch(
                                        searchTerm,
                                        setSearchResults,
                                        false,
                                    );
                                }}
                            />
                        </FormControl>

                        {searchResults && (
                            <SearchResults
                                searchResults={searchResults}
                                onSelectUser={selectUser}
                            />
                        )}
                    </div>
                )}
                {mode === 'invite' && (
                    <div>
                        <FormControl
                            variant="standard"
                            className={classes.formControl}
                            margin="normal"
                        >
                            <TextField
                                variant="standard"
                                autoFocus
                                margin="normal"
                                id="email"
                                label="Email address"
                                type="email"
                                placeholder=""
                                autoComplete="off"
                                style={{ width: 350 }}
                                value={email}
                                onChange={(event) =>
                                    setEmail(event.target.value)
                                }
                                onKeyPress={(event) => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault();
                                        inviteUser(email);
                                    }
                                }}
                            />
                        </FormControl>
                    </div>
                )}
            </DialogContent>
            {mode === 'edit' && (
                <DialogActions>
                    <Button onClick={onClose} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={updateUserTeam} color="primary" autoFocus>
                        Update
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
}

async function deleteUser(userId, dispatch) {
    try {
        const response = await Api.deleteUser(userId);
        if (response.status === 204) {
            dispatch({ type: 'notify', message: 'User deleted' });
            return true;
        } else {
            dispatch({
                type: 'notify',
                message: `User delete failed: ${response.status} ${
                    (response.data && response.data.error) ||
                    response.statusText ||
                    response.statusMessage
                }`,
            });
        }
    } catch (error) {
        dispatch({ type: 'notify', message: `User delete failed: ${error}` });
    }
}

async function deleteUserTotp(userId, dispatch) {
    try {
        const response = await Api.deleteUserTotp(userId);
        if (response.status === 204) {
            dispatch({ type: 'notify', message: 'User totp removed' });
            return true;
        } else {
            dispatch({
                type: 'notify',
                message: `User totp remove failed: ${response.status} ${
                    (response.data && response.data.error) ||
                    response.statusText ||
                    response.statusMessage
                }`,
            });
        }
    } catch (error) {
        dispatch({
            type: 'notify',
            message: `User totp remove failed: ${error}`,
        });
    }
}

async function clearUserSessions(userId, dispatch) {
    try {
        const response = await Api.clearUserSessions(userId);
        if (response.status === 204) {
            dispatch({ type: 'notify', message: 'User sessions cleared' });
            return true;
        } else {
            dispatch({
                type: 'notify',
                message: `Clearing user sessions failed : ${response.status} ${
                    (response.data && response.data.error) ||
                    response.statusText ||
                    response.statusMessage
                }`,
            });
        }
    } catch (error) {
        dispatch({
            type: 'notify',
            message: `Clearing user sessions failed: ${error}`,
        });
    }
}

async function deleteUserCredential(userId, credentialId, dispatch) {
    try {
        const response = await Api.deleteUserCredential(userId, credentialId);
        if (response.status === 204) {
            dispatch({ type: 'notify', message: 'User credential removed' });
            return true;
        } else {
            dispatch({
                type: 'notify',
                message: `User credential remove failed: ${response.status} ${
                    (response.data && response.data.error) ||
                    response.statusText ||
                    response.statusMessage
                }`,
            });
        }
    } catch (error) {
        dispatch({
            type: 'notify',
            message: `User credential remove failed: ${error}`,
        });
    }
}

async function addUserPassword(userId, dispatch) {
    try {
        const response = await Api.addUserPassword(userId);
        if (response.status === 204) {
            dispatch({ type: 'notify', message: 'Password added' });
            return true;
        } else {
            dispatch({
                type: 'notify',
                message: `User password create failed: ${response.status} ${
                    (response.data && response.data.error) ||
                    response.statusText ||
                    response.statusMessage
                }`,
            });
        }
    } catch (error) {
        dispatch({
            type: 'notify',
            message: `User password create failed: ${error}`,
        });
    }
}

function DeleteDialog(props) {
    const {
        open,
        onClose,
        onDelete,
        title,
        text,
        confirmText = 'Delete',
    } = props;

    return (
        <Dialog
            open={!!open}
            onClose={onClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    {text}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Cancel
                </Button>
                <Button onClick={onDelete} color="primary" autoFocus>
                    {confirmText}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function UserToolbar(props) {
    const classes = useStyles();
    const [creatingCustomer, setCreatingCustomer] = useState(false);
    const [deletingUser, setDeletingUser] = useState(false);
    const [removingTotp, setRemovingTotp] = useState(false);
    const [clearingSessions, setClearingSessions] = useState(false);
    const dispatch = useContext(AppDispatch);
    const {
        user,
        hasCustomers,
        totpEnabled,
        hasPassword,
        onUserModified,
        onUserDeleted,
    } = props;

    function handleCloseRemoveConfirmationDialog() {
        setRemovingTotp(false);
    }

    function handleCloseClearSessionsConfirmationDialog() {
        setClearingSessions(false);
    }

    function handleCloseDeleteConfirmationDialog() {
        setDeletingUser(false);
    }

    return (
        <Paper className={classes.toolbar}>
            <Tooltip
                title={hasCustomers ? 'User already belongs to a customer' : ''}
            >
                <span>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={hasCustomers}
                        className={classes.button}
                        onClick={() => setCreatingCustomer(true)}
                    >
                        Create customer
                        <Icon className={classes.rightIcon}>business</Icon>
                    </Button>
                </span>
            </Tooltip>
            <span className={classes.rightButtons}>
                <Tooltip
                    title={
                        totpEnabled
                            ? ''
                            : 'Two factor authentication is not enabled'
                    }
                >
                    <span className={classes.button}>
                        <Button
                            variant="contained"
                            disabled={!totpEnabled}
                            onClick={() => setRemovingTotp(true)}
                        >
                            Remove 2FA
                            <Icon className={classes.rightIcon}>lock</Icon>
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip
                    title={
                        hasPassword ? 'The user already has a password set' : ''
                    }
                >
                    <span className={classes.button}>
                        <Button
                            variant="contained"
                            disabled={!!hasPassword}
                            onClick={async () => {
                                const result = await addUserPassword(
                                    user.id,
                                    dispatch,
                                );

                                if (result && onUserModified) {
                                    onUserModified();
                                }
                            }}
                        >
                            Add password
                            <Icon className={classes.rightIcon}>lock</Icon>
                        </Button>
                    </span>
                </Tooltip>
                <Button
                    variant="contained"
                    onClick={() => setClearingSessions(true)}
                >
                    Clear Sessions
                    <Icon className={classes.rightIcon}>cookie</Icon>
                </Button>
                <Tooltip
                    title={
                        hasCustomers
                            ? 'Remove the user from all teams and customers first'
                            : ''
                    }
                >
                    <span className={classes.button}>
                        <Button
                            variant="contained"
                            disabled={hasCustomers}
                            onClick={() => setDeletingUser(true)}
                        >
                            Delete user
                            <Icon className={classes.rightIcon}>delete</Icon>
                        </Button>
                    </span>
                </Tooltip>
            </span>

            <CustomerDialog
                user={user}
                open={creatingCustomer}
                onClose={() => setCreatingCustomer(false)}
            />

            <DeleteDialog
                open={removingTotp}
                confirmText="Remove"
                onDelete={() => {
                    deleteUserTotp(user.id, dispatch);

                    handleCloseRemoveConfirmationDialog();

                    if (onUserModified) {
                        onUserModified();
                    }
                }}
                onClose={handleCloseRemoveConfirmationDialog}
                title="Remove 2FA?"
                text="Are you sure you want to remove two factor authentication for the user?"
            />

            <DeleteDialog
                open={clearingSessions}
                confirmText="Clear"
                onDelete={() => {
                    clearUserSessions(user.id, dispatch);

                    handleCloseClearSessionsConfirmationDialog();
                }}
                onClose={handleCloseClearSessionsConfirmationDialog}
                title="Clear user sessions?"
                text="All user sessions will be cleared, and the user will have to log in again."
            />

            <DeleteDialog
                open={deletingUser}
                onDelete={() => {
                    deleteUser(user.id, dispatch);

                    handleCloseDeleteConfirmationDialog();

                    if (onUserDeleted) {
                        onUserDeleted();
                    }
                }}
                onClose={handleCloseDeleteConfirmationDialog}
                title="Delete user?"
                text={
                    <React.Fragment>
                        <p>Are you sure you want to delete the user?</p>
                        <p>
                            This will also delete the contact in ActiveCampaign,
                            Reply.IO and add the user to the unsubscribe list in
                            Mailgun.
                        </p>
                    </React.Fragment>
                }
            />
        </Paper>
    );
}

function UserView(props) {
    const [editingMember, setEditingMember] = React.useState(undefined);
    const [removingMember, setRemovingMember] = React.useState(undefined);
    const [removingCredential, setRemovingCredential] =
        React.useState(undefined);
    const [editingUser, setEditingUser] = React.useState(undefined);
    const classes = useStyles();
    const dispatch = useContext(AppDispatch);
    const { userResult, onSelectCustomer, onUserModified, onUserDeleted } =
        props;

    if (!userResult) return 'Loading';

    const { user, customers, teams, memberships, credentials, totpEnabled } =
        userResult;
    const hasPassword =
        credentials &&
        credentials.find((credential) => credential.type === 'password');

    function handleCloseRemoveMemberConfirmationDialog() {
        setRemovingMember(undefined);
    }

    function handleCloseRemoveCredentialConfirmationDialog() {
        setRemovingCredential(undefined);
    }

    function handleCloseEditUserDialog() {
        setEditingUser(undefined);
    }

    return (
        <div>
            <Header title={`User ${user.name || user.email}`} icon="person" />
            <small>{user.id} </small>
            <CopyButton text={user.id} />

            <Stack
                direction="row"
                justifyContent="center"
                spacing={1}
                sx={{ padding: 1 }}
            >
                <Button
                    href={`${datadogAppUrl}/logs?query=index:cloudcraft @uid:${user.id} &agg_q=@action&cols=host,service&index=cloudcraft&messageDisplay=inline&sort_m=&sort_t=&stream_sort=desc&top_n=10&top_o=top&viz=pattern&x_missing=true`}
                    target="_blank"
                    variant="outlined"
                    size="small"
                    color="primary"
                    className={classes.button}
                >
                    Logs
                    <Icon className={classes.rightIcon}>search</Icon>
                </Button>
                <Button
                    href={`${datadogAppUrl}/rum/sessions?query=@usr.id:${user.id}`}
                    target="_blank"
                    variant="outlined"
                    size="small"
                    color="primary"
                    className={classes.button}
                >
                    RUM
                    <Icon className={classes.rightIcon}>play_circle</Icon>
                </Button>
            </Stack>

            <UserToolbar
                user={user}
                hasCustomers={customers.length > 0}
                totpEnabled={totpEnabled}
                hasPassword={hasPassword}
                onUserModified={onUserModified}
                onUserDeleted={onUserDeleted}
            />

            <Box className={classes.groupContainer}>
                <UserTable
                    users={[user]}
                    single
                    onEditUser={() => setEditingUser(user)}
                />
            </Box>

            <Paper className={classes.groupContainer} elevation={1}>
                <Header title="Credentials" />

                <CredentialTable
                    credentials={credentials}
                    onRemoveCredential={async (credentialId) =>
                        setRemovingCredential({ userId: user.id, credentialId })
                    }
                />
            </Paper>

            <Paper className={classes.groupContainer} elevation={1}>
                <Header title="Associations" />

                <GroupHeader
                    title={`Belongs to ${customers.length} customer${
                        customers.length === 1 ? '' : 's'
                    }`}
                    icon="business"
                />
                <CustomerTable
                    customers={customers}
                    onSelectCustomer={onSelectCustomer}
                    initialState={{
                        columns: {
                            columnVisibilityModel: {
                                id: false,
                                ownerName: false,
                                ownerEmail: false,
                                awsMarketplaceId: false,
                                updatedAt: false,
                            },
                        },
                    }}
                />

                <GroupHeader
                    title={`Belongs to ${teams.length} team${
                        teams.length === 1 ? '' : 's'
                    }`}
                    icon="group"
                />
                {teams.map((team) => (
                    <div key={team.id}>
                        <SubHeader title={team.name} />
                        {memberships.map((member) => {
                            if (member.TeamId !== team.id) return null;

                            return (
                                <MemberTable
                                    members={[member]}
                                    single
                                    key={member.id}
                                    onEditMember={(memberId) =>
                                        setEditingMember({ team, member })
                                    }
                                    onRemoveMember={async (memberId) =>
                                        setRemovingMember({
                                            teamId: team.id,
                                            memberId,
                                        })
                                    }
                                />
                            );
                        })}
                    </div>
                ))}
            </Paper>

            <EditUserDialog
                user={editingUser}
                onClose={handleCloseEditUserDialog}
            />

            <MemberDialog
                team={editingMember && editingMember.team}
                mode={editingMember && 'edit'}
                member={editingMember && editingMember.member}
                onClose={(result) => {
                    setEditingMember(undefined);

                    if (result && !result.hasOwnProperty('target')) {
                        onUserModified();
                    }
                }}
            />

            <DeleteDialog
                open={removingMember}
                onDelete={async () => {
                    const result = await removeTeamMember(
                        removingMember.teamId,
                        removingMember.memberId,
                        dispatch,
                    );

                    if (result) {
                        onUserModified();
                    }

                    handleCloseRemoveMemberConfirmationDialog();
                }}
                onClose={handleCloseRemoveMemberConfirmationDialog}
                title="Remove member from team?"
                text="Are you sure you want to remove the member from the team?"
                confirmText="Remove"
            />

            <DeleteDialog
                open={removingCredential}
                onDelete={async () => {
                    const result = await deleteUserCredential(
                        removingCredential.userId,
                        removingCredential.credentialId,
                        dispatch,
                    );

                    if (result) {
                        onUserModified();
                    }

                    handleCloseRemoveCredentialConfirmationDialog();
                }}
                onClose={handleCloseRemoveCredentialConfirmationDialog}
                title="Remove credential from user?"
                text="Are you sure you want to remove the credential from the user?"
                confirmText="Remove"
            />
        </div>
    );
}

function TeamToolbar(props) {
    const dispatch = useContext(AppDispatch);
    const classes = useStyles();
    const [editingTeam, setEditingTeam] = useState(false);
    const [deletingTeam, setDeletingTeam] = useState(false);
    const [editingMembers, setEditingMembers] = useState(false);
    const { team, onTeamModified, onTeamDeleted } = props;

    function handleCloseConfirmationDialog() {
        setDeletingTeam(false);
    }

    return (
        <Paper className={classes.toolbar}>
            <Button
                variant="contained"
                color="primary"
                className={classes.button}
                onClick={() => setEditingTeam(true)}
            >
                Edit team
                <Icon className={classes.rightIcon}>edit</Icon>
            </Button>
            <Button
                variant="contained"
                color="primary"
                className={classes.button}
                onClick={() => setEditingMembers('invite')}
            >
                Invite user by email
                <Icon className={classes.rightIcon}>email</Icon>
            </Button>
            <Button
                variant="contained"
                color="primary"
                className={classes.button}
                onClick={() => setEditingMembers('add')}
            >
                Add direct to team
                <Icon className={classes.rightIcon}>person_add</Icon>
            </Button>
            <Button
                variant="contained"
                className={classes.rightButton}
                onClick={() => setDeletingTeam(true)}
            >
                Delete team
                <Icon className={classes.rightIcon}>delete</Icon>
            </Button>

            <TeamDialog
                team={team}
                open={editingTeam}
                onClose={(result) => {
                    setEditingTeam(false);

                    if (result && !result.hasOwnProperty('target')) {
                        onTeamModified(result);
                    }
                }}
            />

            <MemberDialog
                team={team}
                mode={editingMembers}
                onClose={(result) => {
                    setEditingMembers(undefined);

                    if (result && !result.hasOwnProperty('target')) {
                        onTeamModified(result);
                    }
                }}
            />

            <DeleteDialog
                open={deletingTeam}
                onDelete={() => {
                    const result = deleteTeam(team.id, dispatch);

                    if (result) {
                        handleCloseConfirmationDialog();

                        if (onTeamDeleted) {
                            onTeamDeleted();
                        }
                    }
                }}
                onClose={handleCloseConfirmationDialog}
                title="Delete team?"
                text="Are you sure you want to delete the team?"
            />
        </Paper>
    );
}

async function deleteCustomer(customerId, dispatch) {
    try {
        const response = await Api.deleteCustomer(customerId);
        if (response.status === 204) {
            dispatch({ type: 'notify', message: 'Customer deleted' });
            return true;
        } else {
            dispatch({
                type: 'notify',
                message: `Customer delete failed: ${response.status} ${
                    (response.data && response.data.error) ||
                    response.statusText ||
                    response.statusMessage
                }`,
            });
        }
    } catch (error) {
        dispatch({
            type: 'notify',
            message: `Customer delete failed: ${error}`,
        });
    }
}

function TeamDialog(props) {
    const dispatch = useContext(AppDispatch);
    const classes = useStyles();
    const [name, setName] = useState('');
    const [visible, setVisible] = useState('true');
    const { open, onClose, customer, team } = props;

    useEffect(() => {
        setName(team ? team.name : '');
        setVisible(team ? (team.visible ? 'true' : 'false') : 'true');
    }, [team]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title">
                {team ? 'Edit team' : 'Create team'}
            </DialogTitle>
            <DialogContent>
                <Form id="team-form">
                    <FormControl
                        variant="standard"
                        className={classes.formControl}
                        margin="normal"
                    >
                        <TextField
                            id="name"
                            label="Name"
                            placeholder="Name"
                            type="text"
                            className={classes.textField}
                            margin="normal"
                            variant="outlined"
                            autoFocus={true}
                            autoComplete="off"
                            value={name}
                            onChange={(event) => {
                                const name = event.target.value;
                                setName(name);
                            }}
                        />
                    </FormControl>

                    <FormControl
                        variant="standard"
                        className={classes.formControl}
                        margin="normal"
                    >
                        <FormLabel>Visibility</FormLabel>
                        <RadioGroup
                            value={visible}
                            onChange={(event) => setVisible(event.target.value)}
                            style={{ minWidth: 200 }}
                        >
                            <FormControlLabel
                                value="true"
                                control={<Radio />}
                                label="Visible"
                            />
                            <FormControlLabel
                                value="false"
                                control={<Radio />}
                                label="Secret"
                            />
                        </RadioGroup>
                    </FormControl>
                </Form>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Cancel
                </Button>
                <Button
                    disabled={!name}
                    type="submit"
                    form="team-form"
                    onClick={async () => {
                        let result;
                        if (team) {
                            result = await updateTeam(
                                {
                                    id: team.id,
                                    name,
                                    visible: visible === 'true',
                                },
                                dispatch,
                            );
                        } else {
                            result = await createTeam(
                                {
                                    customerId: customer.id,
                                    ownerId: customer.OwnerId,
                                    name,
                                    visible: visible === 'true',
                                },
                                dispatch,
                            );
                        }

                        onClose(result);
                    }}
                    color="primary"
                >
                    {team ? 'Save team' : 'Create team'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function OwnerDialog(props) {
    const dispatch = useContext(AppDispatch);
    const classes = useStyles();
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState(undefined);
    const { open, onClose, customer } = props;

    const selectUser = async (userId) => {
        const result = await updateCustomer(
            {
                id: customer.id,
                OwnerId: userId,
            },
            dispatch,
            userId !== customer.OwnerId &&
                'Owner changed. Remember to change the email address in Stripe!',
        );

        onClose(result);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title">Change owner</DialogTitle>
            <DialogContent>
                <div>
                    <FormControl
                        variant="standard"
                        className={classes.formControl}
                        margin="normal"
                    >
                        <TextField
                            id="outlined-search"
                            label="Search Users"
                            placeholder="Name, email or ID"
                            type="search"
                            className={classes.textField}
                            margin="normal"
                            variant="outlined"
                            autoFocus={true}
                            autoComplete="off"
                            value={searchTerm}
                            onChange={(event) => {
                                const searchTerm = event.target.value;
                                setSearchTerm(searchTerm);
                                debouncedSearch(
                                    searchTerm,
                                    setSearchResults,
                                    false,
                                );
                            }}
                        />
                    </FormControl>

                    {searchResults && (
                        <SearchResults
                            searchResults={searchResults}
                            onSelectUser={selectUser}
                        />
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

async function deleteSsoConfig(customerId, dispatch) {
    try {
        const response = await Api.deleteSsoConfig(customerId);
        if (response.status === 204) {
            dispatch({ type: 'notify', message: 'SSO config deleted' });
            return true;
        } else {
            dispatch({
                type: 'notify',
                message: `SSO config delete failed: ${response.status} ${
                    (response.data && response.data.error) ||
                    response.statusText ||
                    response.statusMessage
                }`,
            });
        }
    } catch (error) {
        dispatch({
            type: 'notify',
            message: `SSO config delete failed: ${error}`,
        });
    }
}

function SSODialog(props) {
    const dispatch = useContext(AppDispatch);
    const { open, onClose, customer, ssoConfig, teams } = props;
    const [enabled, setEnabled] = useState();
    const [strict, setStrict] = useState();
    const [ssoType, setSsoType] = useState();
    const [idpMetadata, setIdpMetadata] = useState();
    const [idpInitiatedLogin, setIdpInitiatedLogin] = useState();
    const [jitProvisioning, setJitProvisioning] = useState();
    const [domains, setDomains] = useState();
    const [defaultTeamId, setDefaultTeamId] = useState();

    const refresh = () => {
        setEnabled(ssoConfig ? ssoConfig.enabled : false);
        setStrict(ssoConfig ? ssoConfig.strict : false);
        setSsoType(ssoConfig ? ssoConfig.type : 'saml');
        setIdpMetadata(ssoConfig ? ssoConfig.idpMetadata : undefined);
        setIdpInitiatedLogin(ssoConfig ? ssoConfig.idpInitiatedLogin : false);
        setJitProvisioning(ssoConfig ? ssoConfig.jitProvisioning : false);
        setDomains(ssoConfig ? ssoConfig.domains : []);
        setDefaultTeamId(
            ssoConfig
                ? ssoConfig.defaultTeamId
                : teams.length > 0
                ? teams[0].id
                : '',
        );
    };

    useEffect(refresh, [ssoConfig, teams]);

    const canEnableSso =
        (ssoType === 'saml' && !!idpMetadata) ||
        ssoType === 'google' ||
        ssoType === 'datadog';

    const handleIdpMetadataDrop = (files) => {
        if (files.length === 1) {
            const reader = new FileReader();
            reader.onload = (event) => {
                setIdpMetadata(event.target.result);
            };
            reader.readAsText(files[0]);
        }
    };

    const handleClose = () => {
        refresh();
        onClose();
    };

    const router = useRouter();

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth={false}
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title">
                {ssoConfig ? 'Edit SSO' : 'Setup SSO'}
            </DialogTitle>
            <DialogContent>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={enabled}
                            disabled={!canEnableSso}
                            onChange={(event) =>
                                setEnabled(event.target.checked)
                            }
                        />
                    }
                    label="Enabled"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={strict}
                            onChange={(event) =>
                                setStrict(event.target.checked)
                            }
                        />
                    }
                    label="Strict"
                />
                <FormControl variant="standard">
                    <InputLabel>Type</InputLabel>
                    <Select
                        variant="standard"
                        value={ssoType}
                        onChange={(event) => setSsoType(event.target.value)}
                    >
                        <MenuItem value="saml">SAML</MenuItem>
                        <MenuItem value="google">Google</MenuItem>
                        <MenuItem value="datadog">Datadog</MenuItem>
                    </Select>
                </FormControl>
                {ssoType === 'saml' && (
                    <>
                        <DialogSubheader variant="h3">
                            Identity provider metadata
                        </DialogSubheader>
                        <Dropzone
                            onDrop={handleIdpMetadataDrop}
                            multiple={false}
                            accept={{ 'text/xml': [] }}
                            disablePreview
                        >
                            {({
                                isDragActive,
                                getRootProps,
                                getInputProps,
                            }) => (
                                <div
                                    {...getRootProps({
                                        style: {
                                            textAlign: 'center',
                                            borderWidth: 3,
                                            borderStyle: 'dashed',
                                            borderRadius: 12,
                                            ...(isDragActive
                                                ? { borderColor: '#8BC34A' }
                                                : {}),
                                        },
                                    })}
                                >
                                    <input {...getInputProps()} />
                                    {!!idpMetadata ? (
                                        <React.Fragment>
                                            <strong>
                                                {ssoConfig &&
                                                ssoConfig.idpMetadata ===
                                                    idpMetadata ? (
                                                    ssoConfig.idpEntityId
                                                ) : (
                                                    <span
                                                        style={{ color: 'red' }}
                                                    >
                                                        Metadata update pending,
                                                        save to commit
                                                    </span>
                                                )}
                                            </strong>
                                            <br />
                                            To replace{' '}
                                        </React.Fragment>
                                    ) : (
                                        <React.Fragment>
                                            SAML is not configured.
                                            <br />
                                            To update{' '}
                                        </React.Fragment>
                                    )}
                                    configuration drag metadata file here,
                                    <br />
                                    or{' '}
                                    <span style={{ color: 'blue' }}>
                                        select file to upload
                                    </span>
                                </div>
                            )}
                        </Dropzone>

                        <DialogSubheader variant="h3">
                            Service provider details
                        </DialogSubheader>
                        <table>
                            <tbody>
                                <tr style={{ lineHeight: '29px' }}>
                                    <td colSpan={3}>
                                        Settings for app in customer's IdP, or{' '}
                                        <a
                                            href="https://app.cloudcraft.co/saml2/metadata "
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            download the Service Provider
                                            metadata{' '}
                                            <i className="fa fa-external-link" />
                                        </a>
                                    </td>
                                </tr>
                                <tr>
                                    <td>Service Provider Entity ID</td>
                                    <td
                                        style={{
                                            background: '#eee',
                                            padding: 4,
                                        }}
                                    >
                                        https://app.cloudcraft.co/saml2/metadata
                                    </td>
                                </tr>
                                <tr>
                                    <td>Assertion Consumer Service URL</td>
                                    <td
                                        style={{
                                            background: '#eee',
                                            padding: 4,
                                        }}
                                    >
                                        https://app.cloudcraft.co/saml2/assert
                                    </td>
                                </tr>
                                <tr>
                                    <td>NameId Format</td>
                                    <td
                                        style={{
                                            background: '#eee',
                                            padding: 4,
                                        }}
                                    >
                                        urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </>
                )}

                <DialogSubheader variant="h3">
                    Additional features
                </DialogSubheader>
                <Grid container direction="column" spacing={2}>
                    {ssoType === 'saml' && (
                        <Grid item>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={idpInitiatedLogin}
                                        disabled={!canEnableSso}
                                        onChange={(event) =>
                                            setIdpInitiatedLogin(
                                                event.target.checked,
                                            )
                                        }
                                    />
                                }
                                label="Identity Provider (IdP) Initiated Login"
                            />
                        </Grid>
                    )}
                    <Grid item>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={jitProvisioning}
                                    disabled={!canEnableSso}
                                    onChange={(event) =>
                                        setJitProvisioning(event.target.checked)
                                    }
                                />
                            }
                            label="Just-in-Time Provisioning"
                        />
                    </Grid>
                    <Grid item>
                        <TextField
                            variant="standard"
                            label="Domain names (comma separated)"
                            value={domains}
                            style={{ width: 500 }}
                            placeholder="No domains configured"
                            onChange={(event) =>
                                setDomains(
                                    event.target.value
                                        .split(',')
                                        .map(
                                            (domain) => domain && domain.trim(),
                                        ),
                                )
                            }
                        />
                    </Grid>
                    <Grid item>
                        <FormControl variant="standard">
                            <InputLabel>Default team</InputLabel>
                            <Select
                                variant="standard"
                                value={defaultTeamId}
                                disabled={!canEnableSso}
                                onChange={(event) =>
                                    setDefaultTeamId(event.target.value)
                                }
                            >
                                {teams.map((team) => (
                                    <MenuItem key={team.id} value={team.id}>
                                        {team.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} color="primary">
                    Cancel
                </Button>
                {ssoConfig && (
                    <Button
                        type="submit"
                        form="sso"
                        onClick={async () => {
                            const result = await deleteSsoConfig(
                                customer.id,
                                dispatch,
                            );

                            onClose();
                            if (result) {
                                console.log('NAVIGATE on delete', result);
                                router.navigate(
                                    'customer',
                                    { id: customer.id },
                                    { reload: true },
                                ); // Check if force needed
                            }
                        }}
                        color="primary"
                    >
                        Delete
                    </Button>
                )}
                <Button
                    type="submit"
                    form="sso"
                    disabled={!domains || domains.length < 1}
                    onClick={async () => {
                        let result;
                        if (ssoConfig) {
                            result = await updateSsoConfig(
                                customer.id,
                                {
                                    type: ssoType,
                                    enabled,
                                    strict,
                                    idpMetadata,
                                    idpInitiatedLogin,
                                    jitProvisioning,
                                    domains: domains.filter((e) => e),
                                    defaultTeamId,
                                },
                                dispatch,
                            );
                        } else {
                            result = await createSsoConfig(
                                customer.id,
                                {
                                    type: ssoType,
                                    enabled,
                                    strict,
                                    idpMetadata,
                                    idpInitiatedLogin,
                                    jitProvisioning,
                                    domains: domains.filter((e) => e),
                                    defaultTeamId,
                                },
                                dispatch,
                            );
                        }

                        handleClose();
                        if (result) {
                            console.log('NAVIGATE on create', result);
                            router.navigate(
                                'customer',
                                { id: customer.id },
                                { reload: true },
                            ); // Check if force needed
                        }
                    }}
                    color="primary"
                >
                    {customer ? 'Save' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function MergeCustomerDialog(props) {
    const dispatch = useContext(AppDispatch);
    const classes = useStyles();
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState(undefined);
    const { open, onClose, customer } = props;

    const selectSourceCustomer = async (sourceCustomerId) => {
        // customer shown when `merge customer` button clicked is target
        // customer chosen from `Select Customer for Merge` is source
        let result;
        const targetCustomerId = customer.id;
        const sourceCustomer = (
            await Api.getCustomerWithoutChangingState(sourceCustomerId)
        )?.customer;
        if (
            window.confirm(
                `Do you want to merge '${sourceCustomer.name}' users and assets into the '${customer.name}' organization?`,
            )
        ) {
            result = await mergeCustomer(
                targetCustomerId,
                sourceCustomerId,
                dispatch,
            );
        }

        onClose(result);
    };

    const mergeCandidates = (searchResults) => {
        // suppress users returned by search
        const { customers } = searchResults;
        return { users: [], customers };
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title">
                Select Customer for Merge
            </DialogTitle>
            <DialogContent>
                <div>
                    <FormControl
                        variant="standard"
                        className={classes.formControl}
                        margin="normal"
                    >
                        <TextField
                            id="outlined-search"
                            label="Search Customers"
                            placeholder="Name, or ID"
                            type="search"
                            className={classes.textField}
                            margin="normal"
                            variant="outlined"
                            autoFocus={true}
                            autoComplete="off"
                            value={searchTerm}
                            onChange={(event) => {
                                const searchTerm = event.target.value;
                                setSearchTerm(searchTerm);
                                debouncedSearch(searchTerm, setSearchResults); // search customers
                            }}
                        />
                    </FormControl>

                    {searchResults && (
                        <SearchResults
                            searchResults={mergeCandidates(searchResults)}
                            onSelectCustomer={selectSourceCustomer}
                        />
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function CustomerToolbar(props) {
    const classes = useStyles();
    const router = useRouter();
    const [editingCustomer, setEditingCustomer] = useState(false);
    const [deletingCustomer, setDeletingCustomer] = useState(false);
    const [creatingTeam, setCreatingTeam] = useState(false);
    const [changingOwner, setChangingOwner] = useState(false);
    const [mergingCustomer, setMergingCustomer] = useState(false);
    const [editingSso, setEditingSso] = useState(false);
    const dispatch = useContext(AppDispatch);
    const { customer, ssoConfig, teams, onCustomerModified } = props;

    function handleCloseConfirmationDialog() {
        setDeletingCustomer(false);
    }

    return (
        <Paper className={classes.toolbar}>
            <Tooltip title={''}>
                <span>
                    <Button
                        variant="contained"
                        color="primary"
                        className={classes.button}
                        onClick={() => setEditingCustomer(true)}
                    >
                        Edit customer
                        <Icon className={classes.rightIcon}>edit</Icon>
                    </Button>
                </span>
            </Tooltip>
            <Tooltip title={''}>
                <span>
                    <Button
                        variant="contained"
                        color="primary"
                        className={classes.button}
                        onClick={() => setCreatingTeam(true)}
                    >
                        Create team
                        <Icon className={classes.rightIcon}>group_add</Icon>
                    </Button>
                </span>
            </Tooltip>
            <Tooltip title={''}>
                <span>
                    <Button
                        variant="contained"
                        color="primary"
                        className={classes.button}
                        onClick={() => setChangingOwner(true)}
                    >
                        Change owner
                        <Icon className={classes.rightIcon}>edit</Icon>
                    </Button>
                </span>
            </Tooltip>
            <Tooltip title={''}>
                <span>
                    <Button
                        variant="contained"
                        color="primary"
                        className={classes.button}
                        onClick={() => setEditingSso(true)}
                    >
                        {ssoConfig ? 'Edit SSO' : 'Setup SSO'}
                        <Icon className={classes.rightIcon}>edit</Icon>
                    </Button>
                </span>
            </Tooltip>
            <Tooltip title="Migrate customer into this one to merge orgs...">
                <span>
                    <Button
                        variant="contained"
                        color="primary"
                        className={classes.button}
                        onClick={() => setMergingCustomer(true)}
                    >
                        Merge customer
                        <Icon className={classes.rightIcon}>edit</Icon>
                    </Button>
                </span>
            </Tooltip>
            <Tooltip
                title={
                    customer.stripeId
                        ? 'Cannot delete a Customer with a Stripe ID. Cancel the subscription in Stripe and remove the ID before deleting.'
                        : ''
                }
            >
                <span style={{ marginLeft: 'auto' }}>
                    <Button
                        variant="contained"
                        disabled={customer.stripeId}
                        className={classes.rightButton}
                        onClick={() => setDeletingCustomer(true)}
                    >
                        Delete customer
                        <Icon className={classes.rightIcon}>delete</Icon>
                    </Button>
                </span>
            </Tooltip>

            <CustomerDialog
                customer={customer}
                open={editingCustomer}
                onClose={() => setEditingCustomer(false)}
            />
            <TeamDialog
                customer={customer}
                open={creatingTeam}
                onClose={(result) => {
                    setCreatingTeam(false);

                    if (result) {
                        console.log('NAVIGATE on create', result);
                        router.navigate(
                            'customer',
                            { id: customer.id },
                            { reload: true },
                        ); // Check if force needed
                    }
                }}
            />
            <OwnerDialog
                customer={customer}
                open={changingOwner}
                onClose={(result) => {
                    setChangingOwner(false);

                    if (result && !result.hasOwnProperty('target')) {
                        onCustomerModified(false);
                    }
                }}
            />
            <SSODialog
                customer={customer}
                ssoConfig={ssoConfig}
                teams={teams}
                open={editingSso}
                onClose={(result) => {
                    setEditingSso(false);
                }}
            />
            <MergeCustomerDialog
                customer={customer}
                open={mergingCustomer}
                onClose={(result) => {
                    setMergingCustomer(false);
                }}
            />

            <DeleteDialog
                open={deletingCustomer}
                onDelete={() => {
                    deleteCustomer(customer.id, dispatch);

                    handleCloseConfirmationDialog();

                    if (onCustomerModified) {
                        onCustomerModified(true);
                    }
                }}
                onClose={handleCloseConfirmationDialog}
                title="Delete customer?"
                text="Are you sure you want to delete the customer?"
            />
        </Paper>
    );
}

function EmptyCustomerView() {
    return (
        <div>
            <Header title="Customer not found" icon="business" />
        </div>
    );
}

async function deleteTeam(teamId, dispatch) {
    try {
        const response = await Api.deleteTeam(teamId);
        if (response.status === 204) {
            dispatch({ type: 'notify', message: 'Team deleted' });
            return true;
        } else {
            dispatch({
                type: 'notify',
                message: `Team delete failed: ${response.status} ${
                    (response.data && response.data.error) ||
                    response.statusText ||
                    response.statusMessage
                }`,
            });
        }
    } catch (error) {
        dispatch({ type: 'notify', message: `Team delete failed: ${error}` });
    }
}

function customerIsActive(customer) {
    const expiresAt = new Date(customer.activeUntil.replace(' ', 'T'));
    const now = new Date();

    return expiresAt > now;
}

function CustomerView(props) {
    const [editingMember, setEditingMember] = React.useState(undefined);
    const [removingMember, setRemovingMember] = React.useState(undefined);
    const {
        customerResult,
        ssoConfigResult,
        onSelectUser,
        onCustomerModified,
    } = props;
    const classes = useStyles();
    const dispatch = useContext(AppDispatch);
    const router = useRouter();
    if (!customerResult) return 'Loading';

    const { customer, teams } = customerResult;
    const ssoConfig = ssoConfigResult;

    if (!customer) return <EmptyCustomerView />;

    function handleCloseRemoveMemberConfirmationDialog() {
        setRemovingMember(undefined);
    }

    return (
        <div>
            <Header title={`Customer ${customer.name}`} icon="business" />
            <small>{customer.id} </small>
            <CopyButton text={customer.id} />

            <Stack
                direction="row"
                justifyContent="center"
                spacing={1}
                sx={{ padding: 1 }}
            >
                {!customer.trial && customerIsActive(customer) && (
                    <Chip label="ACTIVE" color="success" />
                )}
                {!customerIsActive(customer) && (
                    <Chip label="EXPIRED" color="error" />
                )}
                {customer.trial && (
                    <Chip
                        label={
                            customerIsActive(customer)
                                ? 'TRIALING'
                                : 'WAS TRIAL'
                        }
                        color="warning"
                    />
                )}
                {customer.stripeId && <Chip label="STRIPE" color="info" />}
                {customer.awsMarketplaceId && (
                    <Chip label="AWS MARKETPLACE" color="info" />
                )}
                {customer.ddOrgId && <Chip label="DATADOG ORG" color="info" />}
            </Stack>

            <Stack
                direction="row"
                justifyContent="center"
                spacing={1}
                sx={{ padding: 1 }}
            >
                <Button
                    href={`${datadogAppUrl}/logs?query=index:cloudcraft @cid:${customer.id} &agg_q=@action&cols=host,service&index=cloudcraft&messageDisplay=inline&sort_m=&sort_t=&stream_sort=desc&top_n=10&top_o=top&viz=pattern&x_missing=true`}
                    target="_blank"
                    variant="outlined"
                    size="small"
                    color="primary"
                    className={classes.button}
                >
                    Logs
                    <Icon className={classes.rightIcon}>search</Icon>
                </Button>
                <Button
                    href={`${datadogAppUrl}/rum/sessions?query=@usr.customerId:${customer.id}`}
                    target="_blank"
                    variant="outlined"
                    size="small"
                    color="primary"
                    className={classes.button}
                >
                    RUM
                    <Icon className={classes.rightIcon}>play_circle</Icon>
                </Button>
            </Stack>

            <CustomerToolbar
                customer={customer}
                ssoConfig={ssoConfig}
                teams={teams}
                onCustomerModified={onCustomerModified}
            />

            <Box className={classes.groupContainer}>
                <CustomerTable customers={[customer]} single />
            </Box>

            <Paper className={classes.groupContainer} elevation={1}>
                <Header title="Associations" />

                <GroupHeader
                    title={`Has ${teams.length} Team${
                        teams.length === 1 ? '' : 's'
                    }`}
                />
                {teams.map((team) => (
                    <div key={team.id}>
                        <Header
                            title={`${team.name} (${
                                team.visible ? 'visible' : 'secret'
                            })`}
                            icon="group"
                        />
                        <TeamToolbar
                            team={team}
                            onTeamModified={() => {
                                onCustomerModified();
                            }}
                            onTeamDeleted={() => {
                                router.navigate(
                                    'customer',
                                    { id: customer.id },
                                    { reload: true },
                                ); // Check if force needed
                            }}
                        />
                        <MemberTable
                            members={team.members}
                            onSelectUser={onSelectUser}
                            onEditMember={(memberId) =>
                                setEditingMember({
                                    team,
                                    member: team.members.find(
                                        (member) => member.id === memberId,
                                    ),
                                })
                            }
                            onRemoveMember={async (memberId) =>
                                setRemovingMember({ teamId: team.id, memberId })
                            }
                        />
                    </div>
                ))}
            </Paper>

            <MemberDialog
                team={editingMember && editingMember.team}
                mode={editingMember && 'edit'}
                member={editingMember && editingMember.member}
                onClose={(result) => {
                    setEditingMember(undefined);

                    if (result && !result.hasOwnProperty('target')) {
                        onCustomerModified();
                    }
                }}
            />

            <DeleteDialog
                open={removingMember}
                onDelete={async () => {
                    const result = await removeTeamMember(
                        removingMember.teamId,
                        removingMember.memberId,
                        dispatch,
                    );

                    if (result) {
                        onCustomerModified();
                    }

                    handleCloseRemoveMemberConfirmationDialog();
                }}
                onClose={handleCloseRemoveMemberConfirmationDialog}
                title="Remove member from team?"
                text="Are you sure you want to remove the member from the team?"
                confirmText="Remove"
            />
        </div>
    );
}

function LoginView(props) {
    const classes = useStyles();
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loginError, setLoginError] = useState('');
    const router = useRouter();

    const canSubmit = !loading && !!(email && password);

    const onSubmit = async (e) => {
        e.preventDefault();

        if (!canSubmit) {
            return;
        }

        setLoading(true);
        setLoginError('');
        try {
            const response = await Api.login(email, password);
            if (response.status === 200) {
                console.log('Password login success', response.body);
                router.navigate('search');
            }
            if (response.status === 401) {
                setLoginError(response.data.error);
            }
        } catch (error) {
            setLoginError(`Login failure: ${error}`);
        }
        setLoading(false);
    };

    return (
        <div>
            <form className={classes.form} noValidate onSubmit={onSubmit}>
                <FormControl variant="standard">
                    <TextField
                        variant="standard"
                        autoFocus
                        id="email"
                        label="Email"
                        type="email"
                        placeholder=""
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                    />
                </FormControl>
                <FormControl variant="standard">
                    <TextField
                        variant="standard"
                        id="password"
                        label="Password"
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                    />
                </FormControl>
                <FormControl variant="standard">
                    <Button
                        style={{ marginTop: 32, marginLeft: 0 }}
                        variant="contained"
                        type="submit"
                        color="primary"
                        disabled={!canSubmit}
                        className={classes.button}
                    >
                        Login
                    </Button>
                </FormControl>
            </form>
            {loginError}
        </div>
    );
}

const initialState = { notification: undefined };

function reducer(state, action) {
    console.log('action: ', action);
    switch (action.type) {
        case 'notify':
            return Object.assign({}, state, { notification: action.message });
        default:
            throw new Error('Unknown action specified');
    }
}

const AppDispatch = createContext(null);

const debouncedSearch = debounce(
    (searchTerm, setSearchResults, searchCustomers = true) => {
        searchTerm = searchTerm.trim();

        if (searchTerm.length < 4) {
            setSearchResults(undefined);
            return;
        }

        Api.search(searchTerm, setSearchResults, searchCustomers);
    },
    250,
);

function LoadingView() {
    const classes = useStyles();

    return (
        <div className={classes.app}>
            <CssBaseline />
            <header className={classes.header}>
                <Typography variant="h4">Loading</Typography>
            </header>
        </div>
    );
}

async function createUser(user, dispatch) {
    try {
        const response = await Api.createUser(user);
        if (response.status === 201) {
            dispatch({ type: 'notify', message: 'User created' });
            return response.data;
        } else {
            dispatch({
                type: 'notify',
                message: `User create failed: ${response.status} ${
                    (response.data && response.data.error) ||
                    response.statusText ||
                    response.statusMessage
                }`,
            });
        }
    } catch (error) {
        dispatch({ type: 'notify', message: `User create failed: ${error}` });
    }
}

function SubscriptionDialog(props) {
    const dispatch = useContext(AppDispatch);
    const classes = useStyles();
    const { open, onClose } = props;
    const [name, setName] = useState();
    const [email, setEmail] = useState();
    const [company, setCompany] = useState();
    const [planId, setPlanId] = useState();
    const [quantity, setQuantity] = useState();
    const [activeUntil, setActiveUntil] = useState();
    const [trial, setTrial] = useState();

    useEffect(() => {
        setName('');
        setPlanId('pro_annual_invoiced');
        setQuantity(1);

        const date = addDays(new Date(), 14);
        const defaultEndDate = format(date, "yyyy-MM-dd'T'HH:mm");
        setActiveUntil(defaultEndDate);
        setTrial(false);
    }, []);

    const router = useRouter();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title">
                Create subscription
            </DialogTitle>
            <DialogContent>
                <Form id="subscription-form">
                    <TextField
                        variant="standard"
                        autoFocus
                        margin="normal"
                        id="name"
                        label="User name"
                        type="text"
                        placeholder="Name"
                        autoComplete="off"
                        style={{ width: 350 }}
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                    />
                    <TextField
                        variant="standard"
                        margin="normal"
                        id="email"
                        label="Email"
                        type="email"
                        placeholder="Email"
                        autoComplete="off"
                        style={{ width: 350 }}
                        value={email}
                        onChange={(event) =>
                            setEmail(event.target.value.trim())
                        }
                    />
                    <TextField
                        variant="standard"
                        margin="normal"
                        id="customer"
                        label="Customer name"
                        type="text"
                        placeholder="Company"
                        autoComplete="off"
                        style={{ width: 350 }}
                        value={company}
                        onChange={(event) => setCompany(event.target.value)}
                    />
                    <FormControl
                        variant="standard"
                        className={classes.formControl}
                        margin="normal"
                    >
                        <InputLabel>Plan</InputLabel>
                        <Select
                            variant="standard"
                            value={planId}
                            onChange={(event) => setPlanId(event.target.value)}
                            style={{ minWidth: 200 }}
                        >
                            {getPlans(false, false).map((plan) => (
                                <MenuItem key={plan.planId} value={plan.planId}>
                                    {plan.displayName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        variant="standard"
                        margin="normal"
                        id="quantity"
                        label="Number of seats"
                        type="number"
                        value={quantity}
                        style={{ width: 230 }}
                        onChange={(event) =>
                            setQuantity(parseInt(event.target.value, 10) || '')
                        }
                    />
                    <InputLabel
                        style={{
                            transform: 'translate(0px, 1.5px) scale(0.75)',
                            marginTop: 16,
                        }}
                    >
                        Presets
                    </InputLabel>
                    <div style={{ display: 'flex', flexDirection: 'row' }}>
                        <Button
                            onClick={() =>
                                setActiveUntil(endDateDaysFromNow(14))
                            }
                        >
                            <small>14 days</small>
                        </Button>
                        <Button
                            onClick={() =>
                                setActiveUntil(endDateDaysFromNow(30))
                            }
                        >
                            <small>30 days</small>
                        </Button>
                        <Button
                            onClick={() =>
                                setActiveUntil(endDateYearsFromNow(1))
                            }
                        >
                            <small>1 year</small>
                        </Button>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'end',
                        }}
                    >
                        <TextField
                            variant="standard"
                            margin="normal"
                            label="Subscription end date"
                            type="datetime-local"
                            value={activeUntil}
                            style={{ width: 230 }}
                            onChange={(event) =>
                                setActiveUntil(event.target.value)
                            }
                        />
                    </div>
                    <span>
                        <small>{prettyTime(activeUntil)}</small>
                    </span>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={trial}
                                onChange={(event) =>
                                    setTrial(event.target.checked)
                                }
                            />
                        }
                        label="Trial"
                    />
                </Form>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Cancel
                </Button>
                <Button
                    type="submit"
                    form="subscription-form"
                    disabled={
                        !name ||
                        !email ||
                        !company ||
                        !planId ||
                        !quantity ||
                        !activeUntil
                    }
                    onClick={async () => {
                        const users = await getUserByEmail(email, dispatch);
                        if (users.length <= 1) {
                            const user =
                                users.length > 0
                                    ? users[0]
                                    : await createUser(
                                          { name, email },
                                          dispatch,
                                      );
                            const result = await createCustomer(
                                {
                                    OwnerId: user.id,
                                    name: company,
                                    planId,
                                    quantity,
                                    activeUntil,
                                    trial,
                                    inviteEmail: email,
                                },
                                dispatch,
                            );

                            onClose();
                            if (result) {
                                console.log('NAVIGATE on create', result);
                                router.navigate('customer', { id: result.id });
                            }
                        } else {
                            dispatch({
                                type: 'notify',
                                message: `Email matches ${users.length} users`,
                            });
                        }
                    }}
                    color="primary"
                >
                    Create subscription
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function SearchToolbar(props) {
    const classes = useStyles();
    const [creatingSubscription, setCreatingSubscription] = useState(false);

    return (
        <Paper className={classes.toolbar}>
            <Button
                variant="contained"
                color="primary"
                className={classes.button}
                onClick={() => setCreatingSubscription(true)}
            >
                Create subscription
                <Icon className={classes.rightIcon}>business</Icon>
            </Button>

            <SubscriptionDialog
                open={creatingSubscription}
                onClose={() => setCreatingSubscription(false)}
            />
        </Paper>
    );
}

function ReportToolbar(props) {
    const classes = useStyles();
    const router = useRouter();

    return (
        <Paper className={classes.toolbar}>
            <Button
                variant="contained"
                color="secondary"
                className={classes.button}
                onClick={() =>
                    router.navigate('report', {
                        reportName: 'customer',
                        type: 'active',
                    })
                }
            >
                Active Customer Report
            </Button>
            <Button
                variant="contained"
                color="secondary"
                className={classes.button}
                onClick={() =>
                    router.navigate('report', {
                        reportName: 'customer',
                        type: 'trial',
                    })
                }
            >
                Active Trial Report
            </Button>
        </Paper>
    );
}

function App() {
    const [state, dispatch] = useReducer(reducer, initialState);
    const classes = useStyles();
    const [searchTerm, setSearchTerm] = useState('');
    const [statsResult, setStatsResult] = useState(undefined);
    const [searchResults, setSearchResults] = useState(undefined);
    const [userResult, setUserResult] = useState(undefined);
    const [customerResult, setCustomerResult] = useState(undefined);
    const [reportResult, setReportResult] = useState(undefined);
    const [ssoConfigResult, setSsoConfigResult] = useState(undefined);
    const { route } = useRoute();
    const router = useRouter();

    useEffect(
        () => {
            if (route.name === 'login') {
                setUserResult(undefined);
                setStatsResult(undefined);
                setSearchResults(undefined);
                setReportResult(undefined);
            } else if (route.name === 'search') {
                setUserResult(undefined);
                setReportResult(undefined);
                Api.getStats(setStatsResult);
            } else if (route.name === 'user') {
                Api.getUser(route.params.id, setUserResult);
            } else if (route.name === 'customer') {
                Api.getCustomer(route.params.id, setCustomerResult);
                Api.getSsoConfig(route.params.id, setSsoConfigResult);
            } else if (route.name === 'report') {
                Api.getReport(
                    route.params.reportName,
                    route.params.type,
                    true,
                    setReportResult,
                );
            }
        },
        // The effect is recalculated if the route's name, or the ID changes.
        // The ID is incremented on forcing a reload when navigating, allowing
        // refreshing of the view's data
        [
            route.name,
            route.params.id,
            route.params.reportName,
            route.params.type,
            route.meta.id,
        ],
    );

    const enterSearchMode = () => {
        if (route.name === 'search') return;

        if (searchTerm) {
            debouncedSearch(searchTerm, setSearchResults);
        }
        router.navigate('search');
    };

    const selectCustomer = (customerId) => {
        router.navigate('customer', { id: customerId });
    };

    const selectUser = (userId) => {
        router.navigate('user', { id: userId });
    };

    const onLogout = async (event) => {
        event.stopPropagation();

        const success = await Api.logout();
        if (success) {
            router.navigate('login');
        }
    };

    if (route.name !== 'login') {
        if (
            !statsResult &&
            !searchResults &&
            !userResult &&
            !customerResult &&
            !reportResult
        ) {
            return <LoadingView />;
        }
    }

    return (
        <AppDispatch.Provider value={dispatch}>
            <StyledEngineProvider injectFirst>
                <ThemeProvider theme={theme}>
                    <div className={classes.app}>
                        <CssBaseline />
                        <header
                            className={classes.header}
                            onClick={() => {
                                router.navigateToDefault();
                                setSearchTerm('');
                                setSearchResults(undefined);
                            }}
                        >
                            <img
                                src={logo}
                                className={classes.logo}
                                alt="logo"
                            />
                            <Typography variant="h4">
                                Cloudcraft Admin UI
                            </Typography>
                            {route.name !== 'login' && (
                                <React.Fragment>
                                    <div style={{ flexGrow: 1 }} />
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        color="primary"
                                        onClick={onLogout}
                                    >
                                        Logout
                                    </Button>
                                    <div style={{ width: 24 }} />
                                </React.Fragment>
                            )}
                        </header>
                        <div className={classes.body}>
                            {route.name === 'login' && <LoginView />}

                            {route.name !== 'login' && (
                                <TextField
                                    id="outlined-search"
                                    label="Search Users & Customers"
                                    placeholder="Name, email or ID"
                                    type="search"
                                    className={classes.textField}
                                    margin="normal"
                                    variant="outlined"
                                    autoFocus={route.name === 'search'}
                                    autoComplete="off"
                                    value={searchTerm}
                                    onFocus={(event) => {
                                        enterSearchMode();
                                    }}
                                    onChange={(event) => {
                                        const searchTerm = event.target.value;
                                        setSearchTerm(searchTerm);
                                        debouncedSearch(
                                            searchTerm,
                                            setSearchResults,
                                        );
                                    }}
                                />
                            )}

                            {route.name === 'search' && searchResults && (
                                <SearchResults
                                    searchResults={searchResults}
                                    onSelectUser={selectUser}
                                    onSelectCustomer={selectCustomer}
                                />
                            )}
                            {route.name === 'search' &&
                                !searchResults &&
                                statsResult && (
                                    <div>
                                        <div style={{ marginTop: 32 }}>
                                            <SearchToolbar />
                                        </div>

                                        <div style={{ marginTop: 32 }}>
                                            <ReportToolbar />
                                        </div>

                                        <Header
                                            title={`${statsResult.counts.users} users`}
                                            icon="group"
                                        />
                                        <Header
                                            title={`${statsResult.counts.blueprints} blueprints`}
                                            icon="grid_on"
                                        />
                                    </div>
                                )}

                            {route.name === 'user' &&
                                (userResult && userResult.error ? (
                                    <ErrorMessage
                                        text="Unable to retrieve user"
                                        error={userResult.error}
                                    />
                                ) : (
                                    <UserView
                                        id={route.params.id}
                                        userResult={userResult}
                                        onSelectCustomer={selectCustomer}
                                        onUserDeleted={() => {
                                            setSearchTerm('');
                                            setUserResult(undefined);
                                            setSearchResults(undefined);
                                            Api.getStats(setStatsResult);
                                            router.navigate('search');
                                        }}
                                        onUserModified={() => {
                                            Api.getUser(
                                                route.params.id,
                                                setUserResult,
                                            );
                                        }}
                                    />
                                ))}

                            {route.name === 'customer' && (
                                <CustomerView
                                    id={route.params.id}
                                    customerResult={customerResult}
                                    ssoConfigResult={ssoConfigResult}
                                    onSelectUser={selectUser}
                                    onCustomerModified={async (
                                        customerDeleted,
                                    ) => {
                                        if (customerDeleted) {
                                            setSearchTerm('');
                                            setUserResult(undefined);
                                            setSearchResults(undefined);
                                            Api.getStats(setStatsResult);
                                            router.navigate('search');
                                        } else {
                                            Api.getCustomer(
                                                route.params.id,
                                                setCustomerResult,
                                            );
                                        }
                                    }}
                                />
                            )}

                            {route.name === 'report' &&
                                route.params.reportName === 'customer' && (
                                    <div>
                                        {!reportResult && (
                                            <Header
                                                title="Loading..."
                                                icon="business"
                                            />
                                        )}
                                        {reportResult && (
                                            <Header
                                                title={`${reportResult.length} ${route.params.type} customers`}
                                                icon="business"
                                            />
                                        )}
                                        <CustomerReport
                                            customers={reportResult}
                                            onSelectCustomer={selectCustomer}
                                        />
                                    </div>
                                )}

                            <Notifications notification={state.notification} />
                        </div>
                    </div>
                </ThemeProvider>
            </StyledEngineProvider>
        </AppDispatch.Provider>
    );
}

export default App;
